import { buildQueryRequest } from '@aibi/analysis-engine';
import { AppError, err } from '@aibi/shared';
import type { QueryApi } from './query-api';
import type { QueryResultSource } from './query-result-source';
import type { SemanticModelCatalog } from './semantic-model-catalog';

export interface ServerQueryResultSourceOptions {
  readonly api: QueryApi;
  readonly modelCatalog: SemanticModelCatalog;
  readonly pageSize?: number;
  readonly now?: () => Date;
}

/**
 * Ejecucion REAL en el servidor. Construye un `QueryRequest` con el Analysis State (el
 * servidor re-planifica y valida) y devuelve el resultado tal cual. Si falla, devuelve el
 * error: nunca sustituye los datos por un preview sintetico.
 */
export function createServerQueryResultSource(
  options: ServerQueryResultSourceOptions,
): QueryResultSource {
  const now = options.now ?? (() => new Date());
  return {
    kind: 'server',
    modeFor: () => 'live',
    async load(plan, loadOptions = {}) {
      const { signal, analysisState } = loadOptions;
      if (signal?.aborted === true) return err(new AppError('CANCELLED', 'Consulta cancelada.'));
      if (!analysisState) {
        return err(new AppError('VALIDATION_ERROR', 'Falta el Analysis State de la consulta.'));
      }
      const model = await options.modelCatalog.load(plan.semanticModelId);
      if (!model.ok) return model;
      const built = buildQueryRequest(analysisState, model.value, {
        pagination: { pageSize: options.pageSize ?? 1000 },
        metadata: { origin: 'ui', requestedAt: now().toISOString() },
      });
      if (!built.ok) return built;
      return options.api.execute(built.value.request, signal);
    },
  };
}
